
import React, { createContext, useContext, useState, useCallback } from 'react';

const AnalyticsContext = createContext();

// Default context value for graceful degradation when used outside provider
const defaultAnalyticsValue = {
  track: () => {},
  identify: () => {},
  page: () => {},
  trackPageView: () => {},
  trackCTA: () => {},
  events: [],
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  // Return default values instead of throwing - graceful degradation
  if (!context) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('useAnalytics called outside AnalyticsProvider - using defaults');
    }
    return defaultAnalyticsValue;
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const [events, setEvents] = useState([]);

  const track = useCallback((eventName, properties = {}) => {
    const event = {
      id: Date.now(),
      name: eventName,
      properties,
      timestamp: new Date().toISOString(),
    };

    setEvents(prev => [...prev, event]);

    // In a real application, you would send this to your analytics service
    console.log('Analytics Event:', event);
  }, []);

  const identify = useCallback((userId, traits = {}) => {
    // In a real application, you would identify the user with your analytics service
    console.log('Analytics Identify:', { userId, traits });
  }, []);

  const page = useCallback((pageName, properties = {}) => {
    track('page_view', { page: pageName, ...properties });
  }, [track]);

  // trackPageView - alias for page() that components expect
  const trackPageView = useCallback((pageName, properties = {}) => {
    track('page_view', { page: pageName, ...properties });
  }, [track]);

  // trackCTA - track call-to-action clicks
  const trackCTA = useCallback((page, action, properties = {}) => {
    track('cta_click', { page, action, ...properties });
  }, [track]);

  const value = {
    track,
    identify,
    page,
    trackPageView,
    trackCTA,
    events,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
